<template>
  <div>
    <b-row>
      <b-col class="bg-white py-3" v-if="tools.includes('editor')">
        <div v-if="showSubjectEditor">
          <b-form-input
            v-model="subject"
            class="subject-editor mb-3"
          ></b-form-input>
          <hr />
        </div>

        <document-container
          v-if="document"
          :expand-sections="['body']"
        ></document-container>
      </b-col>

      <b-col v-if="tools.includes('preview')">
        <multiselect
          v-if="showResourceSearch"
          v-model="currentResource"
          :options="groupedResources"
          track-by="id"
          :internal-search="false"
          group-label="label"
          group-values="resources"
          placeholder="Search for applications or contacts"
          :loading="searchLoading"
          @search-change="debounceResourceSearchChange"
          @select="resourceChange"
          @remove="resourceRemove"
          class="mb-3"
        >
          <template slot="singleLabel" slot-scope="{ option }">
            {{ option.label }} <small class="text-muted ml-2">(ID {{ option.id }})</small>
          </template>
          <template slot="option" slot-scope="{ option }">
            <span v-if="option.$isLabel">{{ option.$groupLabel }}</span>
            <span v-else>{{ option.label }} <small class="text-muted ml-2">(ID {{ option.id }})</small></span>
          </template>
        </multiselect>

        <b-alert
          show
          variant="danger"
          v-if="substitutionErrors && substitutionErrors.length > 0"
        >
          <h4>The following substitutions do not have an associated value</h4>

          <ul>
            <li
              v-for="err in substitutionErrors"
              :key="err.id"
            >{{ err.name }}</li>
          </ul>
        </b-alert>

        <div v-if="previewSubject">
          <p class="text-center font-weight-bold">{{ previewSubject }}</p>
          <hr />
        </div>

        <email-preview v-if="document"></email-preview>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { mapState, mapMutations, mapActions } from 'vuex'
import { debounce } from 'lodash'
import Multiselect from 'vue-multiselect'
import EmailPreview from '@/components/LayoutEditor/EmailPreview'
import DocumentContainer from '@/components/LayoutEditor/DocumentContainer'

const debounceResourceSearchTime = 500

export default {
  components: {
    Multiselect,
    EmailPreview,
    DocumentContainer
  },

  props: {
    tools: {
      type: Array,
      default() { return ['editor', 'preview'] }
    },
    showResourceSearch: {
      type: Boolean,
      default: true
    },
    showSubjectEditor: {
      type: Boolean,
      default: true
    }
  },

  mounted() {
    this.fetchAllSubstitutionTags()
    this.preview()
  },

  data() {
    return {
      currentResource: '',
      searchLoading: false
    }
  },

  watch: {
    document: {
      handler(val) {
        this.preview()
      },
      deep: true
    },

    subject(val) {
      this.preview()
    }
  },

  computed: {
    ...mapState('emails', [
      'document',
      'previewSubject',
      'substitutionErrors',
      'resourceType',
      'resourceId'
    ]),

    ...mapState('applications', [
      'applications'
    ]),

    ...mapState('contacts', [
      'contacts'
    ]),

    subject: {
      get() { return this.$store.state.emails.subject },
      set(value) { this.updateSubject(value) }
    },

    groupedResources() {
      return [
        {
          label: 'Applications',
          resources: this.applications
            ? this.applications.map(app => ({
              resourceType: 'application',
              id: app._id,
              label: app.subjectProperties[0].address.description
            }))
            : []
        },
        {
          label: 'Contacts',
          resources: this.contacts
            ? this.contacts.map(contact => ({
              resourceType: 'contact',
              id: contact._id,
              label: `${contact.firstName} ${contact.lastName}`
            }))
            : []
        }
      ]
    }
  },

  methods: {
    ...mapActions('emails', [
      'preview'
    ]),

    ...mapMutations('emails', [
      'updatePreviewHtml',
      'updateSubject',
      'updatePreviewSubject',
      'updateSubstitutionErrors',
      'updateResource',
      'resetResource'
    ]),

    ...mapActions('applications', {
      searchApplications: 'search'
    }),

    ...mapMutations('applications', [
      'resetApplications'
    ]),

    ...mapActions('contacts', {
      searchContacts: 'search'
    }),

    ...mapMutations('contacts', [
      'resetContacts'
    ]),

    ...mapActions('substitution_tags', {
      fetchAllSubstitutionTags: 'fetchAll'
    }),

    async resourceSearchChange(query) {
      if (query && query.length > 0) {
        this.searchLoading = true
        await Promise.all([
          this.searchApplications({ query, campaign: this.$route.name.includes('templates') ? false : this.$route.params.id }),
          this.searchContacts(query)
        ])
        this.searchLoading = false
      }
    },

    debounceResourceSearchChange: debounce(function(query) {
      this.resourceSearchChange(query)
    }, debounceResourceSearchTime),

    resourceChange(resource) {
      this.updateResource({
        resourceType: resource.resourceType,
        resourceId: resource.id
      })
      this.preview()
    },

    resourceRemove(resource) {
      this.resetResource()
      this.resetApplications()
      this.resetContacts()
      this.preview()
    }
  }
}
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
